import React, { useEffect, useRef, useState } from 'react';
import { deleteKPIEmployee, saveKPIEmployees, setKPIValue } from '../../services/api';
import KPICard from './KPICard';
import KpiScaleTable from './KpiScaleTable';
import {
  bonusText, fmtScore, fmtValue, fromInput, money, scoreTone, toInput, unitSuffix
} from './kpiFormat';

const Hint = ({ text }) => (
  <span className="kpi2-hint" tabIndex={0} aria-label={text} data-tip={text}>?</span>
);

const SourceTag = ({ source }) => {
  if (source === 'iiko') return <span className="kpi2-src is-iiko" title="Подтянуто из iiko">iiko</span>;
  if (source === 'manual') return <span className="kpi2-src" title="Внесено вручную">вручную</span>;
  return null;
};

/* Ячейка факта: клик — поле ввода, Enter/уход с поля — сохранить, Esc —
   отменить. Пустое поле стирает значение (показатель снова «не внесён»). */
export const ValueCell = ({ unit, value, source, disabled, onSave, placeholder = 'внести' }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

  const start = () => {
    if (disabled) return;
    setText(toInput(unit, value));
    setEditing(true);
  };

  const commit = async () => {
    const v = fromInput(unit, text);
    if (v === undefined) return;           // не число — оставляем поле открытым
    setEditing(false);
    if (v === value || (v === null && (value === null || value === undefined))) return;
    setSaving(true);
    try { await onSave(v); } catch { /* тост уже показан */ } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <span className="kpi2-input-wrap">
        <input
          ref={inputRef}
          className="kpi2-input"
          inputMode="decimal"
          value={text}
          onChange={e => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            if (e.key === 'Escape') setEditing(false);
          }}
          aria-label="Значение"
        />
        <span className="kpi2-input-unit">{unitSuffix(unit)}</span>
      </span>
    );
  }

  const empty = value === null || value === undefined;
  return (
    <button type="button" className={`kpi2-value ${empty ? 'is-empty' : ''} ${saving ? 'is-saving' : ''}`}
      onClick={start} disabled={disabled} title={disabled ? undefined : 'Нажмите, чтобы изменить'}>
      {empty ? placeholder : fmtValue(unit, value)}
      {!empty && <SourceTag source={source} />}
    </button>
  );
};

const directionWord = (m, to) => (m.lower ? `снизить до ${to}` : `поднять до ${to}`);

const NextTier = ({ result, role, metricsByKey }) => {
  const n = result.next_tier;
  if (!result.complete) {
    return (
      <div className="kpi2-next is-missing">
        Не внесено: {result.missing.map(k => metricsByKey[k]?.name.toLowerCase() || k).join(', ')}.
        {result.partial_score !== null && <> По внесённому — {fmtScore(result.partial_score)} балла.</>}
      </div>
    );
  }
  if (!n) {
    return result.bonus > 0
      ? <div className="kpi2-next is-top">Максимальная ступень премии</div>
      : null;
  }
  const idea = n.ideas[0];
  const m = idea && metricsByKey[idea.key];
  return (
    <div className="kpi2-next">
      До {String(n.min_score).replace('.', ',')} баллов не хватает {String(n.need).replace('.', ',')}
      {' → '}премия {money(n.bonus)}
      {m && <>. Быстрее всего: {m.name.toLowerCase()} — {directionWord(m, fmtValue(m.unit, idea.to_value))}</>}
      {role.bonus.kind === 'percent' && <span className="kpi2-muted"> (при той же выручке)</span>}
    </div>
  );
};

/* Блок одного сотрудника — как в Excel управляющего: показатель, вес,
   факт, балл, вес × балл; внизу итог и премия. */
const EmployeeSheet = ({ emp, role, closed, onSet, title, people, onDelete }) => {
  const metricsByKey = Object.fromEntries(role.metrics.map(m => [m.key, m]));
  const r = emp.result;
  return (
    <article className="kpi2-sheet">
      <header className="kpi2-sheet-head">
        <div>
          <h3>{title || emp.name}</h3>
          {people && <div className="kpi2-sheet-people">{people}</div>}
        </div>
        <div className="kpi2-sheet-total">
          <span className={`kpi2-score-big tone-${scoreTone(r.score)}`}>{fmtScore(r.score)}</span>
          <span className="kpi2-sheet-bonus">{r.complete ? money(r.bonus) : 'премия — после заполнения'}</span>
        </div>
        {onDelete && !closed && (
          <button type="button" className="ui-icon-btn kpi2-sheet-del" onClick={onDelete}
            aria-label={`Убрать ${emp.name} из KPI`} title="Убрать из KPI">×</button>
        )}
      </header>

      <div className="kpi2-sheet-table" role="table">
        <div className="kpi2-sheet-row is-head" role="row">
          <span role="columnheader">Показатель</span>
          <span role="columnheader">Вес</span>
          <span role="columnheader">Факт</span>
          <span role="columnheader">Балл</span>
          <span role="columnheader">Вес × балл</span>
        </div>
        {role.has_revenue && (
          <div className="kpi2-sheet-row is-revenue" role="row">
            <span role="cell" className="kpi2-metric-name">
              Личная выручка <Hint text="Выручка официанта за месяц из iiko. От неё считается премия-процент." />
            </span>
            <span role="cell" className="kpi2-muted">—</span>
            <span role="cell">
              <ValueCell unit="rub" value={emp.revenue || null} source={emp.revenue_source}
                disabled={closed} onSave={v => onSet(emp.id, 'revenue', v)} />
            </span>
            <span role="cell" className="kpi2-muted">база премии</span>
            <span role="cell" />
          </div>
        )}
        {r.rows.map(row => {
          const m = metricsByKey[row.key];
          if (!m) return null;
          return (
            <div key={row.key} className="kpi2-sheet-row" role="row">
              <span role="cell" className="kpi2-metric-name">
                {m.name}
                {m.shared && <span className="kpi2-shared-tag" title="Общий показатель должности">общий</span>}
                <Hint text={m.hint} />
              </span>
              <span role="cell" data-label="Вес">{Math.round(row.weight * 100)}%</span>
              <span role="cell" data-label="Факт">
                {m.parts ? (
                  <span className="kpi2-value is-static">{fmtValue(m.unit, row.value)}</span>
                ) : (
                  <ValueCell unit={m.unit} value={row.value} source={emp.source?.[row.key]}
                    disabled={closed} onSave={v => onSet(m.shared ? 0 : emp.id, row.key, v)} />
                )}
              </span>
              <span role="cell" data-label="Балл">
                <span className={`kpi2-score-pill tone-${scoreTone(row.score)}`}>{row.score ?? '—'}</span>
              </span>
              <span role="cell" data-label="Итог" className="kpi2-weighted">
                {row.weighted === null ? '—' : row.weighted.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}
              </span>
            </div>
          );
        })}
        <div className="kpi2-sheet-row is-total" role="row">
          <span role="cell">Итоговый балл</span>
          <span role="cell" />
          <span role="cell" />
          <span role="cell" />
          <span role="cell"><strong>{fmtScore(r.score)}</strong></span>
        </div>
      </div>

      <NextTier result={r} role={role} metricsByKey={metricsByKey} />
    </article>
  );
};

/* Общие показатели должности: вносятся один раз на всех. Составной
   показатель (такси + питание) вводится по частям. */
const SharedBlock = ({ role, employee, closed, onSet, onlyParts = false }) => {
  const shared = role.metrics.filter(m => m.shared && (!onlyParts || m.parts));
  if (!shared.length) return null;
  const values = employee ? employee.metrics : role.shared_values;
  const source = employee ? employee.source : role.shared_source;
  return (
    <section className="kpi2-card">
      <div className="kpi2-card-head">
        <h3>{onlyParts ? 'Составные показатели' : 'Общие показатели должности'}</h3>
        <p>{onlyParts
          ? 'Показатель складывается из частей: внесите каждую, сумма посчитается сама.'
          : 'Одно значение на всех: вносится здесь один раз и попадает в расчёт каждого сотрудника.'}</p>
      </div>
      <div className="kpi2-shared-grid">
        {shared.map(m => (
          <div key={m.key} className="kpi2-shared-item">
            <div className="kpi2-shared-name">{m.name} <Hint text={m.hint} /></div>
            {m.parts ? (
              <div className="kpi2-parts">
                {m.parts.map(p => (
                  <label key={p.key} className="kpi2-part">
                    <span>{p.name}</span>
                    <ValueCell unit="rub" value={values?.[p.key] ?? null} source={source?.[p.key]}
                      disabled={closed} onSave={v => onSet(0, p.key, v)} />
                  </label>
                ))}
                <div className="kpi2-part-sum">Итого: <strong>{fmtValue(m.unit, values?.[m.key] ?? null)}</strong></div>
              </div>
            ) : (
              <ValueCell unit={m.unit} value={values?.[m.key] ?? null} source={source?.[m.key]}
                disabled={closed} onSave={v => onSet(0, m.key, v)} />
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

const KpiRoleView = ({ data, role: roleKey, onRole, period, closed, run, reload, notify }) => {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [showCard, setShowCard] = useState(false);
  const [showScale, setShowScale] = useState(true);
  const role = data.roles.find(r => r.key === roleKey) || data.roles[0];
  const emps = data.employees.filter(e => e.role === role.key);
  const allShared = role.metrics.every(m => m.shared) && !role.has_revenue;

  const onSet = (employeeId, key, value) => run(
    () => setKPIValue(period.year, period.month, role.key, employeeId, key, value)
  );

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await saveKPIEmployees(role.key, [{ name: name.trim() }]);
      setName('');
      setAdding(false);
      await reload();
      notify('ok', 'Сотрудник добавлен');
    } catch {
      notify('err', 'Не удалось добавить сотрудника');
    }
  };

  const remove = async (emp) => {
    if (!window.confirm(`Убрать «${emp.name}» из KPI? Факт за прошлые месяцы сохранится.`)) return;
    try {
      await deleteKPIEmployee(emp.id);
      await reload();
      notify('ok', 'Сотрудник убран из KPI');
    } catch {
      notify('err', 'Не удалось убрать сотрудника');
    }
  };

  const countBy = (k) => data.employees.filter(e => e.role === k).length;

  return (
    <div className="kpi2-roleview">
      <div className="kpi2-chips" role="tablist" aria-label="Должность">
        {data.roles.map(r => (
          <button key={r.key} type="button" role="tab" aria-selected={r.key === role.key}
            className={`kpi2-chip ${r.key === role.key ? 'active' : ''}`} onClick={() => onRole(r.key)}>
            {r.name}<span className="kpi2-chip-count">{countBy(r.key)}</span>
          </button>
        ))}
      </div>

      <div className="kpi2-role-intro">
        <div>
          <h2>{role.name}</h2>
          <p>{role.about}</p>
        </div>
        <div className="kpi2-role-actions">
          <button type="button" className="ui-btn-ghost" onClick={() => setShowCard(true)}>Карта KPI для сотрудников</button>
          {!closed && <button type="button" className="ui-btn-primary" onClick={() => setAdding(true)}>Добавить сотрудника</button>}
        </div>
      </div>

      {role.issues.length > 0 && (
        <div className="kpi2-note is-warn">Цели должности требуют внимания: {role.issues.join('; ')}.</div>
      )}

      <section className="kpi2-card">
        <button type="button" className="kpi2-collapse" onClick={() => setShowScale(s => !s)} aria-expanded={showScale}>
          <span>
            <h3>Шкала баллов и премия</h3>
            <span className="kpi2-muted">Факт на уровне столбца — столько баллов за показатель. Ниже порога «1» — всё равно 1 балл.</span>
          </span>
          <span className="kpi2-collapse-mark" aria-hidden="true">{showScale ? '−' : '+'}</span>
        </button>
        {showScale && (
          <>
            <KpiScaleTable role={role} />
            <div className="kpi2-bonus-row">
              <span className="kpi2-muted">Премия по итоговому баллу:</span>
              {role.bonus.tiers.map(t => (
                <span key={t.min_score} className="kpi2-bonus-chip">
                  от {String(t.min_score).replace('.', ',')} — {bonusText(role.bonus, t.amount)}
                </span>
              ))}
              <span className="kpi2-muted">ниже {data.min_bonus_score} — без премии</span>
            </div>
          </>
        )}
      </section>

      {!allShared && <SharedBlock role={role} closed={closed} onSet={onSet} />}

      {emps.length === 0 ? (
        <div className="kpi2-empty">
          В этой должности пока никого нет.
          {!closed && <button type="button" className="ui-btn-primary" onClick={() => setAdding(true)}>Добавить сотрудника</button>}
        </div>
      ) : allShared ? (
        <>
          <SharedBlock role={role} employee={emps[0]} closed={closed} onSet={onSet} onlyParts />
          <EmployeeSheet
            emp={emps[0]} role={role} closed={closed} onSet={onSet}
            title={emps.length > 1 ? `Общий расчёт: ${role.name.toLowerCase()}` : emps[0].name}
            people={emps.length > 1 ? `Премия каждому: ${emps.map(e => e.name).join(', ')}` : null}
            onDelete={emps.length === 1 ? () => remove(emps[0]) : null}
          />
          {emps.length > 1 && !closed && (
            <div className="kpi2-people">
              {emps.map(e => (
                <span key={e.id} className="kpi2-person">
                  {e.name}
                  <button type="button" onClick={() => remove(e)} aria-label={`Убрать ${e.name}`}>×</button>
                </span>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="kpi2-sheets">
          {emps.map(emp => (
            <EmployeeSheet key={emp.id} emp={emp} role={role} closed={closed} onSet={onSet}
              onDelete={() => remove(emp)} />
          ))}
        </div>
      )}

      {adding && (
        <div className="ui-modal" onClick={() => setAdding(false)}>
          <form className="ui-modal-box" onClick={e => e.stopPropagation()} onSubmit={add}>
            <div className="ui-modal-head">
              <h2>Сотрудник: {role.name.toLowerCase()}</h2>
              <button type="button" className="ui-icon-btn" onClick={() => setAdding(false)} aria-label="Закрыть">×</button>
            </div>
            <div className="ui-field">
              <label htmlFor="kpi2-new-name">Фамилия и имя</label>
              <input id="kpi2-new-name" list="kpi2-staff" value={name} autoFocus
                onChange={e => setName(e.target.value)} placeholder="Как в iiko и в табеле" />
              <datalist id="kpi2-staff">
                {data.staff_names.map(n => <option key={n} value={n} />)}
              </datalist>
            </div>
            <p className="kpi2-muted">
              Пишите так же, как в iiko и в разделе «Сотрудники»: по имени подтягивается выручка
              официанта и находится строка табеля для премии.
            </p>
            <div className="ui-modal-actions">
              <button type="button" className="ui-btn-ghost" onClick={() => setAdding(false)}>Отмена</button>
              <button type="submit" className="ui-btn-primary kpi2-btn-auto">Добавить</button>
            </div>
          </form>
        </div>
      )}

      {showCard && (
        <KPICard role={role} employees={emps} period={period} minScore={data.min_bonus_score}
          onClose={() => setShowCard(false)} />
      )}
    </div>
  );
};

export default KpiRoleView;
